import { Formik, Form, useFormikContext } from "formik";
import React, { useState } from "react";
import { Alert, InputGroup } from "react-bootstrap";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";

// Helpers etc.
import calculateRatesFormModel from "../../helpers/calculate-rates/form-model";
import initialValues from "../../helpers/calculate-rates/initialValues";
import validationSchema from "../../helpers/calculate-rates/validationSchema";
import { extractErrorMsg, joinClasses } from "../../helpers/utils";
import { useFieldFocus } from "../../hooks/utility";
import { dropoffPickup as dropoffPickupOptions } from "../../constants/dropoffPickup";
import * as pricingActions from "../../store/pricing/actions";
import useCheckZip from "../../hooks/useCheckZip";
import { handleRestrictLimits } from "../../helpers/formikFields";

// Components
import {
  FormikCustomRadios,
  FormikInputGroup,
  // FormikTextField,
} from "../formik/FormikFields";
// import DebugFormikState from "../formik/FormikState";
import { BtnWithLoading } from "../../components/form/SubmitBtn";
import RatesTable from "./RatesTable";
import ZipWithCheckField from "../form/ZipWithCheckField";
import RatesTableSk from "../skeleton/RatesTableSk";
import DisabledLoadingOverlay from "../common/DisabledLoadingOverlay";

const Buttons = styled.div`
  margin-top: 42px;
  flex-direction: column;
  .btn:first-child:not(:last-child) {
    margin-bottom: 16px;
  }
  @media (min-width: 768px) {
    flex-direction: row;
    .btn:first-child:not(:last-child) {
      margin-right: 24px;
      margin-bottom: 0;
    }
  }
`;

const FormStyled = styled(Form)`
  padding: 1.5rem 1rem;
  @media (min-width: 768px) {
    padding: 32px 48px;
  }
`;

const {
  formId,
  formFields: { weight, destinationZip, originatingZip, dropoffPickup },
} = calculateRatesFormModel;

const FormProper = () => {
  const [ratesShown, setRatesShown] = useState(null);
  const { isSubmitting, values, setFieldValue, submitForm, dirty, isValid } =
    useFormikContext();
  const weightRef = useFieldFocus();

  const [handleCheckOrigZip, checkingOrigZip] = useCheckZip(
    originatingZip.label
  );
  const [handleCheckDestZip, checkingDestZip] = useCheckZip(
    destinationZip.label
  );

  const handleShowGroundRate = async () => {
    setFieldValue("showRate", "ground-rate", false);
    await submitForm();
    if (isValid) {
      setRatesShown("ground-rate");
    }
  };

  const handleShowExpressRate = async () => {
    setFieldValue("showRate", "express-rate", false);
    await submitForm();
    if (isValid) {
      setRatesShown("express-rate");
    }
  };

  return (
    <FormStyled id={formId}>
      {/* <FormikTextField
        labelClassname="text-white form-label2"
        formGroupClasses="mb-4"
        name={weight.name}
        label={weight.label}
        type="text"
        size="lg"
        fieldRef={weightRef}
      /> */}
      <FormikInputGroup
        fieldRef={weightRef}
        name={weight.name}
        label={weight.label}
        type="number"
        size="lg"
        max="150"
        min="0"
        step="any"
        formGroupClasses="mb-4"
        inputGroupClasses="appended"
        labelClassname="text-white form-label2"
        append={
          <InputGroup.Append>
            <InputGroup.Text>lbs.</InputGroup.Text>
          </InputGroup.Append>
        }
        handleBlur={handleRestrictLimits}
      />
      <ZipWithCheckField
        labelClassname="text-white form-label2"
        formGroupClasses="mb-4"
        name={originatingZip.name}
        label={originatingZip.label}
        type="text"
        size="lg"
        validate={(val) => handleCheckOrigZip(val)}
        checking={checkingOrigZip}
      />
      <ZipWithCheckField
        labelClassname="text-white form-label2"
        formGroupClasses="mb-4"
        name={destinationZip.name}
        label={destinationZip.label}
        type="text"
        size="lg"
        validate={(val) => handleCheckDestZip(val)}
        checking={checkingDestZip}
      />
      <FormikCustomRadios
        name={dropoffPickup.name}
        label={dropoffPickup.label}
        options={dropoffPickupOptions}
        labelClasses="text-white form-label2 mb-3"
        formGroupClasses="mb-4"
        classes="text-white mb-4 text-body-bigger"
        optionClasses="d-flex"
      />
      {isSubmitting && isEmpty(values.rates) && <RatesTableSk height={757} />}
      {!isEmpty(values.rates) && (
        <DisabledLoadingOverlay
          isLoading={isSubmitting}
          overlayStyles={{ background: "rgba(51,51,51,0.3)", zIndex: 3 }}
        >
          <RatesTable rates={values.rates} shownRate={ratesShown} />
        </DisabledLoadingOverlay>
      )}
      <Buttons
        className={joinClasses([
          "d-flex",
          ratesShown && !isEmpty(values.rates)
            ? "justify-content-center"
            : "justify-content-end",
        ])}
      >
        {((dirty && !isSubmitting) || ratesShown !== "ground-rate") && (
          <BtnWithLoading
            name="submitShowGroundRate"
            variant="light"
            className="text-primary"
            onClick={handleShowGroundRate}
            isSubmitting={isSubmitting && values["showRate"] === "ground-rate"}
            disabled={isSubmitting}
          >
            Show ground rate
          </BtnWithLoading>
        )}
        {((dirty && !isSubmitting) || ratesShown !== "express-rate") && (
          <BtnWithLoading
            name="submitShowExpressRate"
            variant="primary"
            // className="ml-4"
            onClick={handleShowExpressRate}
            isSubmitting={isSubmitting && values["showRate"] === "express-rate"}
            disabled={isSubmitting}
          >
            Show express rate
          </BtnWithLoading>
        )}
      </Buttons>
      {/* <DebugFormikState /> */}
    </FormStyled>
  );
};

const CalculateRatesForm = () => {
  const [submitError, setSubmitError] = useState(null);
  const dispatch = useDispatch();

  const handleSubmit = async (values, { resetForm, setFieldValue }) => {
    try {
      setSubmitError(null);
      const calculateRatesData = {
        ground: (values.showRate === "ground-rate").toString(),
        weight: values.weight.toString(),
        from_zip: values.originatingZip,
        to_zip: values.destinationZip,
        // account_number: "0",
        pickup: (values.dropoffPickup === "schedule-pickup").toString(),
      };
      const rates = await dispatch(
        pricingActions.calculateRates(calculateRatesData)
      );
      setFieldValue("rates", rates.data);
      resetForm({
        values: {
          ...values,
          rates: rates.data,
        },
      });
    } catch (error) {
      // console.log(error);
      setSubmitError(extractErrorMsg(error.message));
    }
  };

  return (
    <>
      {submitError && (
        <Alert variant="danger" className="alert-soft-danger mb-0">
          {submitError}
        </Alert>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <FormProper />
      </Formik>
    </>
  );
};

export default CalculateRatesForm;
